import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, KeyRound, Lock, Play, Radio } from "lucide-react";
import { SectionHeading, Reveal, StatusDot } from "./primitives";
import EventStream from "./EventStream";

const TABS = [
    { id: "stream", label: "Live Stream" },
    { id: "pipeline", label: "Pipeline" },
    { id: "security", label: "Security" },
    { id: "replay", label: "Replay & Debug" },
];

const NODES = [
    { id: "gateway", label: "MDH Gateway", sub: "ingest · auth · route", tone: "cyan" },
    { id: "filter", label: "Event Filter", sub: "rules · transform", tone: "teal" },
    { id: "vault", label: "Signature Vault", sub: "hmac · rotation", tone: "cyan" },
    { id: "endpoint", label: "Your Endpoint", sub: "https · 200 OK", tone: "emerald" },
];

const REPLAY_LINES = [
    "$ mdh events replay evt_9f2ka1 --endpoint api.acme.dev/hooks",
    "→ resolved endpoint   api.acme.dev/hooks (us-east-1)",
    "→ re-signed payload   sha256=8f3a…c41d  key_id=mk_live_42",
    "→ dispatched          200 OK in 8ms",
    "✓ replay complete — idempotency key preserved",
];

function Pipeline() {
    const [active, setActive] = useState("filter");
    const node = NODES.find((n) => n.id === active);
    return (
        <div className="flex h-full flex-col justify-center gap-8 p-6 sm:p-8">
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-0">
                {NODES.map((n, i) => (
                    <div key={n.id} className="flex flex-1 items-center">
                        <button
                            data-testid={`pipeline-node-${n.id}`}
                            onClick={() => setActive(n.id)}
                            className={`w-full rounded-lg border px-3 py-3 text-left transition-all sm:mx-0 ${
                                active === n.id
                                    ? "border-cyan-400/60 bg-cyan-950/40 shadow-[0_0_15px_rgba(6,182,212,0.25)]"
                                    : "border-slate-800 bg-slate-900/50 hover:border-slate-700"
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <StatusDot tone={n.tone === "emerald" ? "emerald" : "cyan"} />
                                <span className="font-mono text-[11px] font-semibold text-slate-200">
                                    {n.label}
                                </span>
                            </div>
                            <div className="mt-1 font-mono text-[10px] text-slate-500">{n.sub}</div>
                        </button>
                        {i < NODES.length - 1 && (
                            <svg
                                className="hidden h-8 w-10 shrink-0 sm:block"
                                viewBox="0 0 40 8"
                                aria-hidden="true"
                            >
                                <line
                                    x1="0"
                                    y1="4"
                                    x2="40"
                                    y2="4"
                                    stroke="rgba(6,182,212,0.5)"
                                    strokeWidth="1.5"
                                    strokeDasharray="6 6"
                                    className="animate-dash-flow"
                                />
                            </svg>
                        )}
                    </div>
                ))}
            </div>
            <div
                data-testid="pipeline-node-detail"
                className="rounded-lg border border-slate-800 bg-slate-950/60 p-5 font-mono text-xs leading-relaxed text-slate-400"
            >
                <span className="text-cyan-400">{node.label}</span>
                <span className="text-slate-600"> — </span>
                {node.id === "gateway" &&
                    "Authenticates the producer, validates schema, and writes to a durable log before acknowledging. p99 ingest: 3ms."}
                {node.id === "filter" &&
                    "Evaluates JSONata-style rules per subscription. Non-matching events are dropped here — your endpoints never see the noise."}
                {node.id === "vault" &&
                    "Signs every delivery with the endpoint's current HMAC key. Keys rotate on schedule with overlap windows, so verification never breaks."}
                {node.id === "endpoint" &&
                    "Delivery confirmed in 8ms. Retries escalate 1s → 5m → 1h over 24h before the event is parked for replay."}
            </div>
        </div>
    );
}

function Security() {
    const [verified, setVerified] = useState(true);
    return (
        <div className="grid h-full gap-6 p-6 sm:grid-cols-2 sm:p-8">
            <div className="rounded-lg border border-slate-800 bg-slate-950/70 p-5 font-mono text-[11px] leading-relaxed">
                <div className="mb-3 flex items-center gap-2 text-slate-500">
                    <Lock className="h-3.5 w-3.5" /> request headers
                </div>
                <pre className="overflow-x-auto text-slate-300">
{`POST /hooks HTTP/2
Host: api.acme.dev
Content-Type: application/json
`}
                    <span className="text-cyan-300">X-MDH-Signature: t=1751328000,</span>
                    {"\n"}
                    <span className="text-cyan-300">  v1=8f3ab…c41d</span>
                    {"\n"}
                    <span className="text-teal-300">X-MDH-Event: payment.succeeded</span>
                    {"\n"}
                    <span className="text-teal-300">X-MDH-Delivery: evt_9f2ka1</span>
                </pre>
            </div>
            <div className="flex flex-col justify-between gap-4">
                <div className="space-y-3">
                    {[
                        [ShieldCheck, "HMAC-SHA256 signed payloads with per-endpoint keys"],
                        [KeyRound, "Automatic key rotation with verification overlap windows"],
                        [Lock, "mTLS pinning + SSRF egress screening on every destination"],
                    ].map(([Icon, text]) => (
                        <div key={text} className="flex items-start gap-3 text-xs text-slate-300">
                            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" />
                            {text}
                        </div>
                    ))}
                </div>
                <button
                    data-testid="security-verify-button"
                    onClick={() => setVerified((v) => !v)}
                    className={`flex items-center justify-between rounded-lg border px-4 py-3 font-mono text-xs transition-all ${
                        verified
                            ? "border-emerald-500/40 bg-emerald-950/40 text-emerald-300"
                            : "border-rose-500/40 bg-rose-950/40 text-rose-300"
                    }`}
                >
                    <span>{verified ? "signature valid — sha256 match" : "signature mismatch"}</span>
                    <span className="text-[10px] uppercase tracking-widest opacity-70">
                        click to toggle
                    </span>
                </button>
            </div>
        </div>
    );
}

function Replay() {
    const [lines, setLines] = useState(REPLAY_LINES.slice(0, 1));
    const [running, setRunning] = useState(false);

    const run = () => {
        if (running) return;
        setRunning(true);
        setLines(REPLAY_LINES.slice(0, 1));
        REPLAY_LINES.slice(1).forEach((l, i) => {
            setTimeout(() => {
                setLines((prev) => [...prev, l]);
                if (i === REPLAY_LINES.length - 2) setRunning(false);
            }, 420 * (i + 1));
        });
    };

    return (
        <div className="flex h-full flex-col p-6 sm:p-8">
            <div className="flex-1 rounded-lg border border-slate-800 bg-slate-950/80 p-5 font-mono text-[11px] leading-loose sm:text-xs">
                {lines.map((l, i) => (
                    <motion.div
                        key={`${l}-${i}`}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={
                            l.startsWith("✓")
                                ? "text-emerald-400"
                                : l.startsWith("$")
                                  ? "text-slate-200"
                                  : "text-slate-500"
                        }
                    >
                        {l}
                    </motion.div>
                ))}
                {running && <span className="inline-block h-3.5 w-2 animate-pulse bg-cyan-400" />}
            </div>
            <button
                data-testid="replay-run-button"
                onClick={run}
                disabled={running}
                className="mt-4 flex w-fit items-center gap-2 rounded-md border border-cyan-500/40 bg-cyan-950/40 px-4 py-2 font-mono text-xs font-semibold text-cyan-300 transition-colors hover:bg-cyan-950/70 disabled:opacity-50"
            >
                <Play className="h-3.5 w-3.5" />
                {running ? "Replaying…" : "Replay failed delivery"}
            </button>
        </div>
    );
}

export default function CommandCenter() {
    const [tab, setTab] = useState("stream");

    return (
        <section
            id="command-center"
            data-testid="command-center-section"
            className="glow-teal-soft relative border-y border-slate-800/70 bg-[#0D1320]/60 py-24 lg:py-32"
        >
            <div className="mx-auto max-w-7xl px-5 lg:px-8">
                <SectionHeading
                    testid="command-center"
                    eyebrow="Command Center"
                    title="Run your webhooks like infrastructure, not hope."
                    copy="One surface for every event, endpoint, signature, and replay. This is a live simulation — pause the stream, trace the pipeline, break the signature."
                />

                <Reveal delay={0.15} className="mt-12">
                    <div className="glass-panel overflow-hidden rounded-xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]">
                        <div className="flex items-center gap-1 overflow-x-auto border-b border-slate-800/80 px-3 pt-2">
                            {TABS.map((t) => (
                                <button
                                    key={t.id}
                                    data-testid={`command-center-tab-${t.id}`}
                                    onClick={() => setTab(t.id)}
                                    className={`relative whitespace-nowrap rounded-t-md px-4 py-2.5 font-mono text-xs transition-colors ${
                                        tab === t.id
                                            ? "text-cyan-300"
                                            : "text-slate-500 hover:text-slate-300"
                                    }`}
                                >
                                    <span className="flex items-center gap-2">
                                        {t.id === "stream" && (
                                            <Radio className="h-3 w-3 text-emerald-400" />
                                        )}
                                        {t.label}
                                    </span>
                                    {tab === t.id && (
                                        <motion.span
                                            layoutId="cc-tab-underline"
                                            className="absolute inset-x-2 bottom-0 h-px bg-cyan-400 shadow-[0_0_8px_rgba(6,182,212,0.8)]"
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="min-h-[380px]">
                            {tab === "stream" && <EventStream testid="cc-event-stream" />}
                            {tab === "pipeline" && <Pipeline />}
                            {tab === "security" && <Security />}
                            {tab === "replay" && <Replay />}
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
